// Helpers para gerar JSON-LD. Usado dentro de head() das rotas SEO.

import type { SeoListing } from "./seo-data";
import type { SeoStats } from "./seo-format";
import { fmtCarTitle } from "./seo-format";

export const SITE_URL = "https://www.buscablindados.com";

/**
 * Aggregator landing page schema: ItemList + page-level AggregateOffer.
 *
 * History note: we used to emit per-item `@type: Vehicle` with name, year, km,
 * price, brand. Google's structured-data validator flagged 5 errors per item
 * (image required, description recommended, GTIN/brand identifier, shipping,
 * return policy) — none of which we can satisfy without lying or exposing
 * upstream URLs we deliberately redact. Switched to bare `ListItem` items
 * (position + name only) so the validator stays happy and the page-level
 * AggregateOffer carries the rich-snippet payload Google actually uses
 * ("R$ 80k–R$ 360k · 740 ofertas").
 */
export function itemListJsonLd(
  name: string,
  url: string,
  listings: SeoListing[],
  stats?: { priceMin?: number | null; priceMax?: number | null; total?: number | null; brand?: string },
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    url,
    numberOfItems: stats?.total ?? listings.length,
    ...(stats?.priceMin != null && stats?.priceMax != null
      ? {
          aggregateOffer: {
            "@type": "AggregateOffer",
            offerCount: stats.total ?? listings.length,
            lowPrice: stats.priceMin,
            highPrice: stats.priceMax,
            priceCurrency: "BRL",
          },
        }
      : {}),
    itemListElement: listings.slice(0, 24).map((l, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: fmtCarTitle(l.display_title ?? l.title),
    })),
  };
}

export function faqJsonLd(faqs: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbJsonLd(crumbs: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url,
    })),
  };
}

export function softwareApplicationJsonLd() {
  // Schema for the home page so Google understands us as a SaaS product
  // (vs. just a "website"). Helps surface us for "alerta carro blindado"
  // type queries and gives the knowledge panel a richer entry.
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Busca Blindados",
    url: SITE_URL,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Busque, compare e clique direto no anunciante. Monitoramos 60+ sites de carros blindados no Brasil — alerta, comparador e histórico de preço são grátis. Plano Pro libera múltiplos alertas automáticos.",
    offers: [
      {
        "@type": "Offer",
        name: "Grátis",
        price: "0",
        priceCurrency: "BRL",
        description: "Busca, comparador, histórico de preço e 1 alerta.",
      },
      {
        "@type": "Offer",
        name: "Pro",
        price: "29",
        priceCurrency: "BRL",
        description: "Assinatura mensal. Até 4 alertas com múltiplos modelos cada.",
      },
    ],
  };
}

/**
 * WebSite schema — emitted on the homepage. Establishes site identity for
 * Google and is the entry point for the future sitelinks searchbox (we'd
 * add `potentialAction` with a SearchAction once /buscar accepts free-text
 * queries).
 */
export function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Busca Blindados",
    url: SITE_URL,
    inLanguage: "pt-BR",
  };
}

/**
 * Organization schema — emitted on the homepage. Improves the knowledge
 * graph card (the right-hand panel on Google for branded queries) and gives
 * us a place to declare canonical logo + social profiles when we have them.
 */
export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Busca Blindados",
    url: SITE_URL,
    logo: "https://static.buscablindados.com/logo.png",
    description:
      "Plataforma de busca, comparação e alerta de carros blindados usados no Brasil. Monitoramos 60+ revendas e marketplaces.",
    // Add Instagram / X / Facebook URLs here when accounts exist — Google
    // uses sameAs to link the knowledge panel to those profiles.
    sameAs: [] as string[],
  };
}

/**
 * Dataset schema for /tabela — the monthly price reference.
 *
 * `Dataset` (not `Table` or `Article`) because that is what the page is: a
 * recurring measurement with a stated method, coverage and update cadence.
 * It also makes the page eligible for Google Dataset Search, which indexes
 * almost no Brazilian used-car data.
 *
 * Deliberately no `distribution`: that property promises a machine-readable
 * download at a URL, and the full table is the gated lead magnet. Declaring a
 * download we don't serve would be a lie to the crawler and an invitation to
 * scrape the thing we ask for an email in exchange for. `isAccessibleForFree`
 * stays true because the page's own 15 rows genuinely are.
 */
export function priceReferenceDatasetJsonLd(opts: {
  url: string;
  /** First day of the edition month, `YYYY-MM-DD`. */
  editionMonth: string;
  /** Measurement date, `YYYY-MM-DD` — not the publish date. */
  generatedAt: string;
  /** Minimum distinct listings for a cohort to be published. */
  minN: number;
}) {
  const publisher = {
    "@type": "Organization",
    name: "Busca Blindados",
    url: SITE_URL,
  };

  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: "Tabela Busca Blindados",
    alternateName: "Tabela de preço do carro blindado usado",
    description:
      "Faixa de preço pedido por carros blindados usados no Brasil, agrupada por modelo, versão e ano: piso (percentil 10), mediana e teto (percentil 90), mais quilometragem mediana e proporção de anúncios que já reduziram o preço. A tabela FIPE precifica o veículo de fábrica e não considera a blindagem; esta série mede o mercado de blindados diretamente.",
    url: opts.url,
    license: "https://creativecommons.org/licenses/by/4.0/",
    isAccessibleForFree: true,
    creator: publisher,
    publisher,
    inLanguage: "pt-BR",
    datePublished: opts.generatedAt,
    dateModified: opts.generatedAt,
    temporalCoverage: opts.editionMonth.slice(0, 7),
    spatialCoverage: { "@type": "Place", name: "Brasil" },
    // Monthly edition, built on the first day of each month.
    accrualPeriodicity: "P1M",
    keywords: [
      "carro blindado usado",
      "preço de carro blindado",
      "tabela de preço",
      "blindagem automotiva",
      "mercado automotivo brasileiro",
    ],
    measurementTechnique: `Agregação de anúncios ativos coletados de revendas e marketplaces brasileiros, deduplicados por veículo, agrupados por modelo, versão e ano-modelo. Apenas grupos com ao menos ${opts.minN} anúncios distintos são publicados.`,
    variableMeasured: [
      { "@type": "PropertyValue", name: "Piso (percentil 10 do preço pedido)", unitText: "BRL" },
      { "@type": "PropertyValue", name: "Mediana do preço pedido", unitText: "BRL" },
      { "@type": "PropertyValue", name: "Teto (percentil 90 do preço pedido)", unitText: "BRL" },
      { "@type": "PropertyValue", name: "Quilometragem mediana", unitText: "KMT" },
      { "@type": "PropertyValue", name: "Anúncios distintos no grupo" },
      { "@type": "PropertyValue", name: "Proporção de anúncios com redução de preço", unitText: "P1" },
    ],
  };
}

/**
 * Same as `itemListJsonLd` but used for stand-alone state pages that don't
 * have a single brand. Convenience wrapper so route files don't need to know
 * the option-bag shape.
 */
export function stateItemListJsonLd(
  name: string,
  url: string,
  listings: SeoListing[],
  stats: SeoStats,
) {
  return itemListJsonLd(name, url, listings, {
    priceMin: stats.priceMin,
    priceMax: stats.priceMax,
    total: stats.total,
  });
}
